/**
 * Comprehensive Error Handling System for ПОМОГАЙ.ONLINE Frontend
 * Types, classes, and utilities for centralized error management
 */

export type ErrorType =
  // Network errors
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'NO_INTERNET'
  | 'DNS_ERROR'
  | 'SSL_ERROR'
  // HTTP errors
  | 'BAD_REQUEST'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'VALIDATION_ERROR'
  | 'SERVER_ERROR'
  | 'SERVICE_UNAVAILABLE'
  // Auth errors
  | 'AUTH_FAILED'
  | 'TOKEN_EXPIRED'
  | 'INVALID_TOKEN'
  | 'SESSION_EXPIRED'
  | 'INVALID_CREDENTIALS'
  // Validation errors
  | 'INVALID_EMAIL'
  | 'INVALID_PHONE'
  | 'INVALID_CARD'
  | 'PASSWORD_MISMATCH'
  | 'WEAK_PASSWORD'
  | 'FIELD_REQUIRED'
  // Business logic errors
  | 'INSUFFICIENT_BALANCE'
  | 'LIMIT_EXCEEDED'
  | 'DUPLICATE_ENTRY'
  | 'OPERATION_NOT_ALLOWED'
  | 'RESOURCE_LOCKED'
  // Device errors
  | 'PERMISSION_DENIED'
  | 'CAMERA_UNAVAILABLE'
  | 'GPS_UNAVAILABLE'
  // App errors
  | 'PARSE_ERROR'
  | 'REQUEST_CANCELLED'
  | 'UNKNOWN';

export type ErrorCategory =
  | 'network'
  | 'authentication'
  | 'validation'
  | 'business'
  | 'device'
  | 'application';

export interface AppError {
  type: ErrorType;
  message: string;
  userMessage: string;
  statusCode?: number;
  originalError?: unknown;
  context?: Record<string, unknown>;
  timestamp: Date;
  isRetryable: boolean;
  requiresReauth: boolean;
  category: ErrorCategory;
}

export interface ErrorLog {
  id: string;
  error: AppError;
  url?: string;
  userId?: string;
  stackTrace?: string;
}

export interface RetryConfig {
  maxRetries: number;
  delayMs: number;
  backoffMultiplier: number;
  retryableStatusCodes: number[];
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  delayMs: 1000,
  backoffMultiplier: 2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

export const ERROR_MESSAGES: Record<ErrorType, string> = {
  NETWORK_ERROR: 'Network error. Check your internet connection.',
  TIMEOUT: 'Request timeout. Please try again.',
  NO_INTERNET: 'No internet connection.',
  DNS_ERROR: 'DNS error. Check the server address.',
  SSL_ERROR: 'SSL error. Please update the app.',
  BAD_REQUEST: 'Invalid data.',
  UNAUTHORIZED: 'Authorization required. Please sign in.',
  FORBIDDEN: 'You do not have access to this resource.',
  NOT_FOUND: 'Resource not found.',
  CONFLICT: 'Data conflict.',
  VALIDATION_ERROR: 'Data validation error.',
  SERVER_ERROR: 'Server error. Please try again later.',
  SERVICE_UNAVAILABLE: 'Service temporarily unavailable.',
  AUTH_FAILED: 'Authentication error.',
  TOKEN_EXPIRED: 'Session expired. Please sign in again.',
  INVALID_TOKEN: 'Invalid token.',
  SESSION_EXPIRED: 'Session expired.',
  INVALID_CREDENTIALS: 'Invalid email or password.',
  INVALID_EMAIL: 'Invalid email format.',
  INVALID_PHONE: 'Invalid phone format.',
  INVALID_CARD: 'Invalid card format.',
  PASSWORD_MISMATCH: 'Passwords do not match.',
  WEAK_PASSWORD: 'Password is too weak.',
  FIELD_REQUIRED: 'Required field.',
  INSUFFICIENT_BALANCE: 'Insufficient funds.',
  LIMIT_EXCEEDED: 'Limit exceeded.',
  DUPLICATE_ENTRY: 'Entry already exists.',
  OPERATION_NOT_ALLOWED: 'Operation not allowed.',
  RESOURCE_LOCKED: 'Resource is locked.',
  PERMISSION_DENIED: 'Access denied.',
  CAMERA_UNAVAILABLE: 'Camera unavailable.',
  GPS_UNAVAILABLE: 'Geolocation unavailable.',
  PARSE_ERROR: 'Data parsing error.',
  REQUEST_CANCELLED: 'Request was cancelled.',
  UNKNOWN: 'An unknown error occurred.',
};

export function getErrorCategory(type: ErrorType): ErrorCategory {
  const categoryMap: Record<ErrorType, ErrorCategory> = {
    NETWORK_ERROR: 'network',
    TIMEOUT: 'network',
    NO_INTERNET: 'network',
    DNS_ERROR: 'network',
    SSL_ERROR: 'network',
    UNAUTHORIZED: 'authentication',
    AUTH_FAILED: 'authentication',
    TOKEN_EXPIRED: 'authentication',
    INVALID_TOKEN: 'authentication',
    SESSION_EXPIRED: 'authentication',
    INVALID_CREDENTIALS: 'authentication',
    VALIDATION_ERROR: 'validation',
    INVALID_EMAIL: 'validation',
    INVALID_PHONE: 'validation',
    INVALID_CARD: 'validation',
    PASSWORD_MISMATCH: 'validation',
    WEAK_PASSWORD: 'validation',
    FIELD_REQUIRED: 'validation',
    INSUFFICIENT_BALANCE: 'business',
    LIMIT_EXCEEDED: 'business',
    DUPLICATE_ENTRY: 'business',
    OPERATION_NOT_ALLOWED: 'business',
    RESOURCE_LOCKED: 'business',
    PERMISSION_DENIED: 'device',
    CAMERA_UNAVAILABLE: 'device',
    GPS_UNAVAILABLE: 'device',
    BAD_REQUEST: 'application',
    FORBIDDEN: 'application',
    NOT_FOUND: 'application',
    CONFLICT: 'application',
    SERVER_ERROR: 'application',
    SERVICE_UNAVAILABLE: 'application',
    PARSE_ERROR: 'application',
    REQUEST_CANCELLED: 'application',
    UNKNOWN: 'application',
  };
  return categoryMap[type] || 'application';
}

export function isRetryableError(type: ErrorType): boolean {
  const retryableTypes: ErrorType[] = [
    'NETWORK_ERROR',
    'TIMEOUT',
    'NO_INTERNET',
    'SERVER_ERROR',
    'SERVICE_UNAVAILABLE',
  ];
  return retryableTypes.includes(type);
}

export function requiresReauth(type: ErrorType): boolean {
  const authTypes: ErrorType[] = [
    'UNAUTHORIZED',
    'TOKEN_EXPIRED',
    'INVALID_TOKEN',
    'SESSION_EXPIRED',
  ];
  return authTypes.includes(type);
}

export function getErrorIcon(type: ErrorType): string {
  const icons: Record<ErrorType, string> = {
    NETWORK_ERROR: 'wifi_off',
    TIMEOUT: 'schedule',
    NO_INTERNET: 'wifi_off',
    DNS_ERROR: 'dns',
    SSL_ERROR: 'security',
    UNAUTHORIZED: 'lock',
    AUTH_FAILED: 'lock_open',
    TOKEN_EXPIRED: 'timer',
    INVALID_TOKEN: 'vpn_key',
    SESSION_EXPIRED: 'timer_off',
    INVALID_CREDENTIALS: 'person',
    VALIDATION_ERROR: 'error_outline',
    INVALID_EMAIL: 'email',
    INVALID_PHONE: 'phone',
    INVALID_CARD: 'credit_card',
    PASSWORD_MISMATCH: 'lock',
    WEAK_PASSWORD: 'security',
    FIELD_REQUIRED: 'star',
    INSUFFICIENT_BALANCE: 'account_balance_wallet',
    LIMIT_EXCEEDED: 'trending_up',
    DUPLICATE_ENTRY: 'content_copy',
    OPERATION_NOT_ALLOWED: 'block',
    RESOURCE_LOCKED: 'lock',
    PERMISSION_DENIED: 'block',
    CAMERA_UNAVAILABLE: 'camera_alt',
    GPS_UNAVAILABLE: 'location_off',
    BAD_REQUEST: 'warning',
    FORBIDDEN: 'do_not_disturb',
    NOT_FOUND: 'search_off',
    CONFLICT: 'conflict',
    SERVER_ERROR: 'cloud_off',
    SERVICE_UNAVAILABLE: 'cloud_off',
    PARSE_ERROR: 'construction',
    REQUEST_CANCELLED: 'cancel',
    UNKNOWN: 'error',
  };
  return icons[type] || 'error';
}
